import classNames from 'classnames';
import * as React from 'react';
import { Spinner } from './Spinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'plain';
  loading?: boolean;
  active?: boolean;
  textAlign?: 'center' | 'left' | 'right';
  hideContentDuringLoading?: boolean;
  icon?: string;
  focusable?: boolean;
  onClick?: (() => void | Promise<void>) | ((e?: any | undefined) => void | Promise<void>);
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      loading,
      textAlign = 'center',
      disabled,
      hideContentDuringLoading = false,
      icon,
      active,
      onClick,
      focusable = true,
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={classNames(
          'transition-all',
          'text-sm leading-[22px] select-none font-medium',
          'flex flex-row items-center  outline-1 outline-offset-1',
          'rounded',
          {
            'text-white bg-gray-900 hover:bg-gray-700 disabled:bg-gray-200 ': variant === 'primary',
            'text-gray-900 hover:bg-gray-200 border border-solid': variant === 'secondary',
            'border-gray-200': variant === 'secondary' && !className?.includes('border-'),
            'disabled:bg-white disabled:opacity-36':
              variant === 'secondary' && !className?.includes('disabled:'),
            'text-blue-500 hover:bg-blue-700/[.06] focus:bg-blue-700/[.12] disabled:text-gray-900 disabled:opacity-36':
              variant === 'plain',
            'justify-center text-center': textAlign === 'center',
            'justify-start text-left': textAlign === 'left',
            'justify-end text-right': textAlign === 'right',
            'w-full': !className?.includes('w-'),
            'min-w-[73px]': !className?.includes('min-w-'),
            'py-[9px]': !className?.includes('py-'),
            'px-4': !className?.includes('px-'),
            'min-h-[40px]': !className?.includes('min-h-'),
            'outline-gray-900 outline': active === true,
            'focus:outline-gray-900 focus:outline': focusable,
          },
          className,
        )}
        disabled={disabled || (loading && disabled === undefined)}
        onClick={!disabled ? onClick : undefined}
        {...rest}
      >
        {loading && (
          <Spinner
            variant="white"
            size="sm"
            className={classNames({
              'mr-0': hideContentDuringLoading,
            })}
          />
        )}
        {!hideContentDuringLoading && !loading && (
          <>
            {icon && <img src={icon} alt="Loading..." className="w-6 h-6 min-w-6 min-h-6 mr-2" />}
            {children}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
